/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useStyles } from './ContactsDrawer.styles';
import { contactsAPI } from '../../contactsAPI';
import { loadContacts, loadContactDetail, closeContactDetail, showSavedContactDetail, deleteContactAction, toggleFavorite, addNote, updateNote, deleteNote, loadGroups, loadBlocked, setSearch, setTab, setGroupFilter } from '../../actions';
import { contactDetailScope } from '../../contactDetailRecovery';
import GoogleContactsImport from './GoogleContactsImport';
import { ContactDetail } from './ContactsDrawerDetail';
import { ContactEditForm } from './ContactsDrawerForm';
import { DuplicateSetCard, GroupsPanel } from './ContactsDrawerPanels';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { contactSearchAddKind, contactSearchAddText } from '../../../../../shared/contact-search-add';
import { getCurrentClientId, getInitials, parseCSV, parseVCard, pickAvatarColor } from './contactsDrawerUtils';
export default function ContactsDrawer({ onClose, onInviteContact, showInviteActions, onCallContact, callLabel = 'Call', showCallHistory = true, embedded = false }) {
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    // Redux state
    const { contacts, groups, blocked, isLoading: loading, error, search, activeTab, selectedGroupId, contactDetail, selectedContactId, isDetailLoading, detailError } = useSelector((state) => state['features/contacts']);
    // Local-only UI state
    const [callError, setCallError] = useState('');
    const [addingNew, setAddingNew] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [editorBusy, setEditorBusy] = useState(false), [editorNotice, setEditorNotice] = useState('');
    const editorPending = useRef(false);
    const editorOpen = addingNew || Boolean(editingContact);
    const handleEditorBusy = useCallback((busy) => { editorPending.current = busy; setEditorBusy(busy); }, []);
    const [duplicates, setDuplicates] = useState([]);
    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [contactRequests, setContactRequests] = useState([]);
    const [requestQuery, setRequestQuery] = useState('');
    const [requestMessage, setRequestMessage] = useState('');
    const [requestError, setRequestError] = useState('');
    const [requestSaving, setRequestSaving] = useState(false);
    const [myHandle, setMyHandle] = useState('');
    const [handleError, setHandleError] = useState('');
    const [handleSaving, setHandleSaving] = useState(false);
    const [favoriteBusy, setFavoriteBusy] = useState(false);
    const [favoriteError, setFavoriteError] = useState('');
    const favoritePending = useRef(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const deletePending = useRef(false);
    const deleteRetryButton = useRef(null);
    const mounted = useRef(true);
    // ============================================
    // DATA LOADING
    // ============================================
    // The search that the list on screen answers: a "no match" is real only
    // for that search (the Add "<text>" row).
    const [listedSearch, setListedSearch] = useState(null);
    useEffect(() => {
        let current = true;
        setListedSearch(null);
        Promise.resolve(dispatch(loadContacts(search || undefined, selectedGroupId || undefined, activeTab === 'favorites' ? true : undefined))).then(() => { if (current && mounted.current)
            setListedSearch(search); }, () => { });
        return () => { current = false; };
    }, [search, selectedGroupId, activeTab === 'favorites', dispatch]);
    useEffect(() => {
        dispatch(loadGroups());
    }, [dispatch]);
    useEffect(() => {
        if (activeTab === 'blocked') {
            dispatch(loadBlocked());
        }
    }, [activeTab, dispatch]);
    useEffect(() => {
        if (activeTab === 'duplicates') {
            contactsAPI.findDuplicates().then(data => setDuplicates(data.duplicates || [])).catch(() => { });
        }
    }, [activeTab]);
    useEffect(() => {
        contactsAPI.listRequests().then(data => setContactRequests(data.requests || [])).catch(() => { });
        contactsAPI.listHandles().then(data => {
            const primary = data.handles.find(h => h.is_primary) || data.handles[0];
            if (primary?.handle) {
                setMyHandle(`@${primary.handle.replace(/^@+/, '')}`);
            }
        }).catch(() => { });
    }, []);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; dispatch(closeContactDetail()); }; }, [dispatch]);
    useEffect(() => {
        if (deleteError?.contactId === selectedContactId)
            deleteRetryButton.current?.focus();
    }, [deleteError, selectedContactId]);
    // ============================================
    // ALPHABETICAL GROUPING
    // ============================================
    // Favorites sort first, in their own section; there is no Favorites tab
    // (Robert, 2026-11-02, as the phone apps).
    const grouped = useMemo(() => {
        const map = new Map();
        const favorites = contacts.filter(c => Boolean(c.is_favorite));
        for (const c of contacts) {
            if (c.is_favorite)
                continue;
            const letter = (c.name?.[0] || '#').toUpperCase();
            if (!map.has(letter))
                map.set(letter, []);
            map.get(letter).push(c);
        }
        const letters = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
        return favorites.length ? [['Favorites', favorites], ...letters] : letters;
    }, [contacts]);
    // A search with no match: one first row, Add "<text>" (shared/contact-search-add.js).
    const searchAddText = contactSearchAddText(search, {
        listReady: listedSearch === search && !loading && !error,
        matches: contacts.length,
        trash: activeTab !== 'all' && activeTab !== 'favorites'
    });
    const searchAddKind = contactSearchAddKind(searchAddText);
    const [addPrefill, setAddPrefill] = useState('');
    const requestField = useRef(null);
    const focusRequestField = useRef(false);
    useEffect(() => {
        if (activeTab === 'requests' && focusRequestField.current) {
            focusRequestField.current = false;
            requestField.current?.focus();
        }
    }, [activeTab]);
    const openAdd = (text = '') => {
        if (editorPending.current)
            return;
        setEditorNotice('');
        // An @handle: Request a contact, filled; a found account gets the request.
        if (contactSearchAddKind(text) === 'handle') {
            setRequestQuery(text);
            setRequestError('');
            focusRequestField.current = true;
            dispatch(setTab('requests'));
            return;
        }
        setAddPrefill(text);
        setAddingNew(true);
        setEditingContact(null);
    };
    // ============================================
    // ACTIONS
    // ============================================
    const handleToggleFavorite = useCallback(async (e, contact) => {
        e.stopPropagation();
        if (favoritePending.current || deletePending.current || !mounted.current)
            return;
        favoritePending.current = true;
        setFavoriteBusy(true);
        setFavoriteError('');
        try {
            await dispatch(toggleFavorite(contact.id, contact.is_favorite));
        }
        catch (error) {
            if (mounted.current)
                setFavoriteError(error instanceof Error ? error.message : 'Favorite change not confirmed. Refresh Contacts.');
        }
        finally {
            favoritePending.current = false;
            if (mounted.current)
                setFavoriteBusy(false);
        }
    }, [dispatch]);
    // Robert, 2026-10-05: Delete moves the contact to the trash (Hide contact hides a
    // directory contact) at once, with no question and no notice after it.
    const handleDelete = useCallback(async (contactId) => {
        if (deletePending.current || favoritePending.current || !mounted.current)
            return;
        deletePending.current = true;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await dispatch(deleteContactAction(contactId));
        }
        catch {
            if (mounted.current)
                setDeleteError({ contactId,
                    message: contactId.startsWith('platform-') ? 'Not hidden.' : 'Not moved to trash.' });
        }
        finally {
            deletePending.current = false;
            if (mounted.current)
                setDeleteBusy(false);
        }
    }, [dispatch]);
    const handleMerge = useCallback(async (primaryId, secondaryIds) => {
        try {
            await contactsAPI.merge(primaryId, secondaryIds);
            contactsAPI.findDuplicates().then(data => setDuplicates(data.duplicates || [])).catch(() => { });
            dispatch(loadContacts());
        }
        catch {
            // Silently fail
        }
    }, [dispatch]);
    const handleImportFile = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        setImporting(true);
        setImportResult(null);
        try {
            const scope = contactDetailScope();
            const text = await file.text();
            let rows = [];
            if (file.name.endsWith('.csv')) {
                rows = parseCSV(text);
            }
            else if (file.name.endsWith('.vcf') || file.name.endsWith('.vcard')) {
                rows = parseVCard(text);
            }
            else {
                throw new Error('Unsupported file type. Use .csv or .vcf');
            }
            if (!mounted.current || contactDetailScope() !== scope)
                throw new Error('Your account changed.');
            const result = await contactsAPI.import(rows);
            if (!mounted.current || contactDetailScope() !== scope)
                return;
            setImportResult(result);
            dispatch(loadContacts());
            dispatch(loadGroups());
        }
        catch (err) {
            setImportResult({
                imported: 0,
                skipped: 0,
                errors: [{ name: 'File', error: err instanceof Error ? err.message : 'Import failed' }]
            });
        }
        finally {
            setImporting(false);
        }
    }, [dispatch]);
    const handleUnblock = useCallback(async (blockId) => {
        try {
            await contactsAPI.unblock(blockId);
            dispatch(loadBlocked());
        }
        catch {
            // Silently fail
        }
    }, [dispatch]);
    const reloadRequests = useCallback(async () => {
        const data = await contactsAPI.listRequests();
        setContactRequests(data.requests || []);
    }, []);
    const handleSendRequest = useCallback(async () => {
        const query = requestQuery.trim();
        const message = requestMessage.trim();
        if (!query) {
            setRequestError('Enter a public handle or VRS number.');
            return;
        }
        setRequestSaving(true);
        setRequestError('');
        try {
            await contactsAPI.sendRequest({ query, message: message || undefined });
            setRequestQuery('');
            setRequestMessage('');
            await reloadRequests();
        }
        catch (err) {
            setRequestError(err instanceof Error ? err.message : 'Could not send contact request.');
        }
        finally {
            setRequestSaving(false);
        }
    }, [reloadRequests, requestMessage, requestQuery]);
    const handleRespondRequest = useCallback(async (requestId, action) => {
        await contactsAPI.respondToRequest(requestId, action);
        await reloadRequests();
        dispatch(loadContacts());
    }, [dispatch, reloadRequests]);
    const handleSaveHandle = useCallback(async () => {
        const handle = myHandle.trim().replace(/^@+/, '');
        if (!handle) {
            setHandleError('Enter a public handle.');
            return;
        }
        setHandleSaving(true);
        setHandleError('');
        try {
            const result = await contactsAPI.updatePrimaryHandle({ handle, visibility: 'public' });
            setMyHandle(`@${result.handle.handle.replace(/^@+/, '')}`);
        }
        catch (err) {
            setHandleError(err instanceof Error ? err.message : 'Could not save handle.');
        }
        finally {
            setHandleSaving(false);
        }
    }, [myHandle]);
    const handleOpenDetail = useCallback((contact) => {
        dispatch(loadContactDetail(contact.id));
    }, [dispatch]);
    const handleCall = useCallback((contact) => {
        if (!contact.phone_number || !mounted.current)
            return;
        setCallError('');
        try {
            if (onCallContact)
                onCallContact(contact);
            else if (queueService?.isConnected())
                queueService.sendP2PCall(contact.phone_number);
            else
                setCallError('No connection. Try again.');
        }
        catch (error) {
            setCallError(error instanceof Error ? error.message : 'The call could not be started. Try again.');
        }
    }, [onCallContact]);
    // ============================================
    // RENDER
    // ============================================
    const tabs = [
        { key: 'all', label: 'All' },
        { key: 'requests', label: 'Requests' },
        { key: 'groups', label: 'Groups' },
        { key: 'import', label: 'Import' },
        { key: 'duplicates', label: 'Duplicates' },
        { key: 'blocked', label: 'Blocked' }
    ];
    return (React.createElement("div", { className: cx(classes.drawer, embedded && classes.drawerEmbedded) },
        React.createElement("div", { className: classes.header },
            React.createElement("span", { className: classes.headerTitle }, "Contacts"),
            React.createElement("div", { className: classes.headerActions },
                onClose && (React.createElement("button", { className: classes.headerBtn, disabled: editorBusy, onClick: () => { if (!editorPending.current)
                        onClose(); }, type: 'button' }, "Close")),
                React.createElement("button", { "aria-label": 'Add contact', className: classes.addIconBtn, disabled: editorOpen || deleteBusy || favoriteBusy, onClick: () => openAdd(), title: 'Add contact', type: 'button' },
                    React.createElement(PersonPlusIcon, null)))),
        !editorOpen && React.createElement("div", { style: {
                display: 'flex',
                overflowX: 'auto',
                padding: '6px 16px',
                gap: '4px',
                borderBottom: `1px solid var(--border-color, rgba(255,255,255,0.1))`
            } }, tabs.map(tab => (React.createElement("button", { className: cx(classes.headerBtn, activeTab === tab.key && classes.headerBtnActive), key: tab.key, "aria-pressed": activeTab === tab.key, onClick: () => dispatch(setTab(tab.key)), onFocus: e => e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' }), style: { flexShrink: 0, scrollMarginInline: 4 }, type: 'button' }, tab.label)))),
        !editorOpen && (activeTab === 'all' || activeTab === 'favorites') && (React.createElement("div", { className: classes.searchBox },
            React.createElement("input", { className: classes.searchInput, onChange: e => dispatch(setSearch(e.target.value)), "aria-label": 'Search contacts', placeholder: 'Search contacts...', type: 'text', value: search }))),
        !editorOpen && activeTab === 'all' && groups.length > 0 && (React.createElement("div", { className: classes.groupFilterRow },
            React.createElement("button", { className: cx(classes.groupChip, !selectedGroupId && classes.groupChipActive), onClick: () => dispatch(setGroupFilter(null)), "aria-pressed": !selectedGroupId, type: 'button' }, "All"),
            groups.map(g => (React.createElement("button", { className: cx(classes.groupChip, selectedGroupId === g.id && classes.groupChipActive), key: g.id, onClick: () => dispatch(setGroupFilter(g.id === selectedGroupId ? null : g.id)), "aria-pressed": selectedGroupId === g.id, type: 'button' },
                g.name,
                " (",
                g.member_count,
                ")"))))),
        (deleteBusy || deleteError?.contactId === selectedContactId) && (React.createElement("div", { style: { padding: '8px 16px', flexShrink: 0, overflowWrap: 'anywhere' } },
            deleteBusy && React.createElement("div", { role: 'status' }, "Deleting contact\u2026"),
            deleteError?.contactId === selectedContactId && React.createElement(React.Fragment, null,
                React.createElement("div", { role: 'alert' }, deleteError.message),
                React.createElement("button", { className: classes.headerBtn, disabled: deleteBusy || favoriteBusy || isDetailLoading || Boolean(detailError), onClick: () => handleDelete(deleteError.contactId), ref: deleteRetryButton, type: 'button' }, "Try again")))),
        editorNotice && !editorOpen && React.createElement("div", { role: 'status', style: { padding: '8px 16px' } }, editorNotice),
        React.createElement("div", { className: classes.body },
            callError && React.createElement("div", { role: 'alert', style: { padding: '8px 16px' } }, callError),
            favoriteBusy && React.createElement("div", { role: 'status' }, "Saving favorite\u2026"),
            favoriteError && React.createElement("div", { role: 'alert' }, favoriteError),
            (activeTab === 'all' || activeTab === 'favorites') && !selectedContactId && !addingNew && (React.createElement(React.Fragment, null,
                loading && React.createElement("div", { className: classes.loadingSpinner }, "Loading contacts..."),
                error && React.createElement("div", { className: classes.emptyState },
                    React.createElement("div", { role: 'alert' }, error),
                    React.createElement("button", { className: classes.headerBtn, disabled: loading, onClick: () => dispatch(loadContacts()), type: 'button' }, "Retry Contacts")),
                searchAddText && React.createElement(React.Fragment, null,
                    React.createElement("button", { className: cx(classes.contactRow, classes.searchAddRow), onClick: () => openAdd(searchAddText), type: 'button' },
                        React.createElement("span", { "aria-hidden": true, className: classes.searchAddIcon },
                            React.createElement(PersonPlusIcon, null)),
                        React.createElement("span", { className: classes.contactName }, `Add “${searchAddText}”`)),
                    searchAddKind === 'phone' && onCallContact && (React.createElement("button", { className: cx(classes.contactRow, classes.searchAddRow), onClick: () => handleCall({ name: '', phone_number: searchAddText.replace(/[\s().-]/g, '') }), type: 'button' },
                        React.createElement("span", { "aria-hidden": true, className: classes.searchAddIcon },
                            React.createElement(PhoneIcon, null)),
                        React.createElement("span", { className: classes.contactName }, `${callLabel} ${searchAddText}`)))),
                !searchAddText && !loading && !error && contacts.length === 0 && (React.createElement("div", { className: classes.emptyState }, search ? 'No contacts match your search.' : 'No contacts yet. Add one or import from a file.')),
                !loading && !error && grouped.map(([letter, items]) => (React.createElement("div", { key: letter },
                    React.createElement("div", { className: classes.sectionHeader }, letter),
                    items.map(c => (React.createElement("div", { className: classes.contactRow, key: c.id },
                        React.createElement("button", { "aria-label": `Open ${c.name}`, className: classes.contactOpen, onClick: () => handleOpenDetail(c), type: 'button' },
                            React.createElement("div", { className: classes.avatar, style: { backgroundColor: pickAvatarColor(c.name, c.avatar_color) } }, getInitials(c.name)),
                            React.createElement("div", { className: classes.contactInfo },
                                React.createElement("div", { className: classes.contactName }, c.name),
                                React.createElement("div", { className: classes.contactSub }, c.phone_number || c.email || c.organization || 'No details'))),
                        React.createElement("button", { "aria-label": `${c.is_favorite ? 'Remove' : 'Add'} ${c.name} ${c.is_favorite ? 'from' : 'to'} favorites`, "aria-pressed": Boolean(c.is_favorite), className: classes.favoriteStar, disabled: favoriteBusy || c.id.startsWith('platform-'), onClick: e => handleToggleFavorite(e, c), style: { background: 'transparent', border: 0, minWidth: 44, minHeight: 44 }, type: 'button' }, c.is_favorite ? '\u2605' : '\u2606'),
                        (onCallContact || (showInviteActions && onInviteContact)) && (React.createElement("div", { className: classes.contactActions },
                            c.phone_number && (React.createElement("button", { "aria-label": callLabel === 'Call' ? `Call ${c.name}` : `${callLabel} for ${c.name}`, className: classes.iconBtn, onClick: e => { e.stopPropagation(); handleCall(c); }, title: callLabel, type: 'button' }, callLabel)),
                            showInviteActions && onInviteContact && React.createElement("button", { className: classes.iconBtn, onClick: e => { e.stopPropagation(); onInviteContact(c); }, title: 'Invite to room', type: 'button' }, "Invite"))))))))))),
            selectedContactId && !editorOpen && (React.createElement("div", { "aria-busy": isDetailLoading, style: { padding: '12px 16px' } },
                isDetailLoading && React.createElement("div", { role: 'status' }, "Loading contact details\u2026"),
                detailError && React.createElement("div", { role: 'alert' }, detailError),
                detailError && React.createElement("button", { className: classes.headerBtn, disabled: isDetailLoading, onClick: () => dispatch(loadContactDetail(selectedContactId)), type: 'button' }, "Retry contact details"),
                !contactDetail && React.createElement("button", { className: classes.headerBtn, onClick: () => dispatch(closeContactDetail()), type: 'button' }, "Back to Contacts"))),
            contactDetail && !editorOpen && (React.createElement(ContactDetail, { key: contactDetail.contact.id, onCallContact: handleCall, callLabel: callLabel, showCallHistory: showCallHistory, readOnly: deleteBusy || favoriteBusy || isDetailLoading || Boolean(detailError), onRefresh: () => dispatch(loadContactDetail(contactDetail.contact.id)), contact: contactDetail.contact, timeline: contactDetail.timeline || [], notes: contactDetail.notes || [], groups: groups, onBack: () => dispatch(closeContactDetail()), onDelete: handleDelete, onEdit: c => { if (!deletePending.current && !favoritePending.current) {
                    setEditorNotice('');
                    setEditingContact(c);
                    setAddingNew(false);
                } }, onToggleFavorite: handleToggleFavorite, onAddNote: (content) => dispatch(addNote(contactDetail.contact.id, content)), onUpdateNote: (noteId, content) => dispatch(updateNote(contactDetail.contact.id, noteId, content)), onDeleteNote: (noteId) => dispatch(deleteNote(contactDetail.contact.id, noteId)) })),
            (addingNew || editingContact) && (React.createElement(ContactEditForm, { key: editingContact?.id || `new:${addPrefill}`, onBusyChange: handleEditorBusy, contact: editingContact, prefill: editingContact ? '' : addPrefill, groups: groups, onCancel: () => { setAddingNew(false); setEditingContact(null); }, onSaved: (contactId, notice, detail) => {
                    setEditorNotice(notice || 'Contact saved.');
                    setAddingNew(false);
                    setEditingContact(null);
                    if (detail)
                        dispatch(showSavedContactDetail(detail));
                    else if (contactId)
                        dispatch(loadContactDetail(contactId));
                    else
                        dispatch(closeContactDetail());
                    dispatch(loadContacts());
                    dispatch(loadGroups());
                } })),
            !editorOpen && activeTab === 'requests' && (React.createElement("div", { style: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' } },
                React.createElement("div", { style: { padding: '12px', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px' } },
                    React.createElement("div", { className: classes.detailSectionTitle }, "Your public handle"),
                    React.createElement("div", { style: { display: 'flex', gap: '8px' } },
                        React.createElement("input", { className: classes.formInput, onChange: e => setMyHandle(e.target.value), placeholder: '@yourname', type: 'text', value: myHandle }),
                        React.createElement("button", { className: classes.saveBtn, disabled: handleSaving, onClick: handleSaveHandle, type: 'button' }, handleSaving ? 'Saving...' : 'Save')),
                    handleError && React.createElement("div", { className: cx(classes.importResult, classes.importError) }, handleError)),
                React.createElement("div", { style: { padding: '12px', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px' } },
                    React.createElement("div", { className: classes.detailSectionTitle }, "Request a contact"),
                    React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                        React.createElement("input", { className: classes.formInput, onChange: e => setRequestQuery(e.target.value), placeholder: '@handle or VRS number', ref: requestField, type: 'text', value: requestQuery }),
                        React.createElement("textarea", { className: classes.formInput, onChange: e => setRequestMessage(e.currentTarget.value || ''), placeholder: 'Optional message', rows: 3, value: requestMessage }),
                        requestError && React.createElement("div", { className: cx(classes.importResult, classes.importError) }, requestError),
                        React.createElement("button", { className: classes.saveBtn, disabled: requestSaving, onClick: handleSendRequest, type: 'button' }, requestSaving ? 'Sending...' : 'Send Request'))),
                React.createElement("div", null,
                    React.createElement("div", { className: classes.detailSectionTitle }, "Pending requests"),
                    contactRequests.filter(req => req.status === 'pending').length === 0 ? (React.createElement("div", { className: classes.emptyState }, "No pending contact requests.")) : (contactRequests.filter(req => req.status === 'pending').map(req => {
                        const currentClientId = getCurrentClientId();
                        const incoming = currentClientId ? req.recipient_client_id === currentClientId : false;
                        const displayName = incoming ? req.requester_name : req.recipient_name;
                        const handle = incoming ? req.requester_handle : req.recipient_handle;
                        const phone = incoming ? req.requester_phone_number : req.recipient_phone_number;
                        return (React.createElement("div", { className: classes.blockedRow, key: req.id },
                            React.createElement("div", null,
                                React.createElement("div", { className: classes.blockedInfo }, displayName || 'Contact request'),
                                React.createElement("div", { className: classes.blockedReason },
                                    incoming ? 'Incoming' : 'Outgoing',
                                    " ",
                                    handle ? `@${handle}` : phone || '')),
                            incoming && (React.createElement("div", { style: { display: 'flex', gap: '6px' } },
                                React.createElement("button", { className: classes.saveBtn, onClick: () => handleRespondRequest(req.id, 'accept'), type: 'button' }, "Accept"),
                                React.createElement("button", { className: classes.cancelBtn, onClick: () => handleRespondRequest(req.id, 'reject'), type: 'button' }, "Reject")))));
                    }))))),
            !editorOpen && activeTab === 'groups' && (React.createElement(GroupsPanel, { groups: groups, onRefresh: () => dispatch(loadGroups()) })),
            !editorOpen && activeTab === 'import' && (React.createElement("div", { style: { padding: '16px' } },
                React.createElement("div", { style: { marginBottom: '16px', padding: '12px', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px' } },
                    React.createElement("div", { className: classes.detailSectionTitle }, "Import from Google"),
                    React.createElement(GoogleContactsImport, { onImported: () => { dispatch(loadContacts()); dispatch(loadGroups()); } })),
                React.createElement("div", { className: classes.detailSectionTitle }, "Import from File"),
                React.createElement("div", { className: classes.importDropZone, onClick: () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.csv,.vcf,.vcard';
                        input.onchange = (e) => {
                            const fe = e;
                            handleImportFile(fe);
                        };
                        input.click();
                    }, role: 'button' },
                    React.createElement("p", { style: { color: 'var(--text-color, #fff)', fontSize: '14px' } }, importing ? 'Importing...' : 'Click to browse CSV or VCard files'),
                    React.createElement("p", { style: { color: 'var(--text-muted, #888)', fontSize: '12px', marginTop: '4px' } },
                        "CSV columns: Name, Phone, Email, Organization",
                        React.createElement("br", null),
                        "VCard (.vcf) format also supported")),
                importResult && (React.createElement("div", null,
                    importResult.imported > 0 && (React.createElement("div", { className: cx(classes.importResult, classes.importSuccess) },
                        "Imported ",
                        importResult.imported,
                        " contact",
                        importResult.imported > 1 ? 's' : '')),
                    importResult.skipped > 0 && (React.createElement("div", { className: classes.importResult },
                        "Skipped ",
                        importResult.skipped,
                        " duplicate",
                        importResult.skipped > 1 ? 's' : '')),
                    importResult.errors.map((err, i) => (React.createElement("div", { className: cx(classes.importResult, classes.importError), key: i },
                        err.name,
                        ": ",
                        err.error))))))),
            !editorOpen && activeTab === 'duplicates' && (React.createElement("div", { style: { padding: '16px' } }, duplicates.length === 0 ? (React.createElement("div", { className: classes.emptyState }, "No duplicate contacts found.")) : (duplicates.map((dup, di) => (React.createElement(DuplicateSetCard, { dup: dup, key: di, onMerge: handleMerge })))))),
            !editorOpen && activeTab === 'blocked' && (React.createElement("div", null, blocked.length === 0 ? (React.createElement("div", { className: classes.emptyState }, "No blocked contacts.")) : (blocked.map(b => (React.createElement("div", { className: classes.blockedRow, key: b.id },
                React.createElement("div", null,
                    React.createElement("div", { className: classes.blockedInfo }, b.blocked_phone || b.blocked_email || b.blocked_client_id),
                    b.reason && React.createElement("div", { className: classes.blockedReason }, b.reason)),
                React.createElement("button", { className: classes.unblockBtn, onClick: () => handleUnblock(b.id), type: 'button' }, "Unblock"))))))))));
}
const ICON_PROPS = {
    'aria-hidden': true,
    fill: 'none',
    focusable: 'false',
    height: 24,
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    strokeWidth: 1.8,
    viewBox: '0 0 24 24',
    width: 24
};
/** Add contact: a person with a plus, the weight of the search icon, no filled circle (Robert, 2026-11-02). */
function PersonPlusIcon() {
    return (React.createElement("svg", { ...ICON_PROPS },
        React.createElement("circle", { cx: '9', cy: '8', r: '3.6' }),
        React.createElement("path", { d: 'M2.8 20c0-3.4 2.8-5.8 6.2-5.8s6.2 2.4 6.2 5.8' }),
        React.createElement("path", { d: 'M19 8.5v6M16 11.5h6' })));
}
function PhoneIcon() {
    return (React.createElement("svg", { ...ICON_PROPS },
        React.createElement("path", { d: 'M6.6 3.8h2.6l1.6 4-2 1.3a10.5 10.5 0 0 0 6.1 6.1l1.3-2 4 1.6v2.6a2 2 0 0 1-2.1 2A15.6 15.6 0 0 1 4.6 5.9a2 2 0 0 1 2-2.1z' })));
}
